import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'kam-utect-pred-vedrem',
    title: 'Kam utéct před vedrem: chalupa, kde se dá v srpnu spát',
    metaTitle: 'Kam utéct před vedrem – tipy na chladné chalupy',
    perex:
      'V bytě je v deset večer devětadvacet a beton sálá do rána. Přitom stačí vyjet o pár set metrů výš nebo do stinného údolí a máte úplně jiné léto. Poradíme, kam a čeho si na chalupě všímat, aby vás horko nedohnalo i tam.',
    coverImage: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Stinná lesní cesta mezi vysokými stromy, kterou prosvítá odpolední slunce',
    publishedAt: '2026-08-02',
    author: 'Redakce Mátevolno',
    tags: ['léto', 'vedro', 'jak vybírat'],
  },
  content: `V bytě je v deset večer devětadvacet, okno otevřené k ničemu a beton pod ním sálá do rána. Klimatizaci nemáte a ventilátor jen přehazuje horký vzduch z jednoho rohu do druhého. Přitom nemusíte jet do Skandinávie – stačí pár set metrů nahoru nebo do údolí, kam slunce v poledne nedosáhne.

> Sto metrů nadmořské výšky znamená zhruba půl stupně dolů. Chalupa v osmi stech metrech je o pět stupňů jiné léto.

## Ve městě je vedro delší, ne jen silnější

Rozdíl mezi městem a venkovem není hlavně v poledni – tam je horko všude. Rozdíl je v noci. Asfalt, beton a zdi se přes den nabijí a pak to celou noc vydávají zpátky, takže se centrum ochladí mnohem méně než pole a les za městem. Proto se v bytě nedá spát a na chalupě si po půlnoci berete deku.

Když k tomu přidáte, že venkovní vzduch nad loukou a lesem je vlhčí a stín tam dělá stín, ne rozpálenou stěnu, vyjde vám jednoduchá rada: v horkém týdnu neřešte program, řešte, kde budete spát.

## Čtyři místa, kde je v Česku o pár stupňů míň

- **Hory.** Nejspolehlivější varianta. Krkonoše, Jeseníky, Šumava, Beskydy – nad sedmi sty metry se v srpnu spí pod peřinou i po tropickém dni.
- **Skalní města a soutěsky.** Kokořínsko, České Švýcarsko, Adršpach. V roklích a soutěskách se drží chladný vzduch a v pískovci je i v poledne příjemně.
- **Údolí řek a hluboké lesy.** Berounka, Sázava, Vltava nad přehradami. Voda a souvislý les drží teplotu dole i tam, kde je nadmořská výška nízká.
- **Sever a vysočina.** Vysočina, Orlické hory, Lužické hory – nejsou vysoké, ale noci tam bývají výrazně chladnější než v nížinách.

::nabidky{label="8" text="Prohlédnout pobyty v horách a přírodě"}

## Ne každá chalupa je v horku k přežití

Tohle se v inzerátu nepíše, a přitom rozhodne o celém pobytu. Kámen, stín a přízemí jsou v srpnu cennější než rozloha.

| Spí se dobře | Spí se špatně |
| --- | --- |
| Kamenná nebo roubená stavba, tlusté zdi | Novostavba s velkými prosklenými plochami |
| Ložnice v přízemí | Ložnice v zatepleném podkroví pod plechem |
| Vzrostlé stromy nad zahradou | Betonová terasa na jih bez stínu |
| Okna na dvě strany, dá se provětrat | Jedno okno na jižní stranu |
| Sklep, kde se dá chladit | Nic chladnějšího než lednice |

Podkroví je největší past. Přes den se nahřeje na čtyřicet a do rána nestihne vychladnout – i v horách. Když jedete v tropech, ptejte se, kde se přesně spí.

## Na co se zeptat před rezervací

- **Je na zahradě stín?** Vzrostlý strom nebo pergola dělá z odpoledne použitelný čas. Na fotkách se to pozná: pokud je zahrada focená za ostrého slunce a nemá kam ustoupit, počítejte s tím.
- **Kde jsou ložnice?** Přízemí a severní strana je v horku výhra, podkroví na jih trest.
- **Jsou v oknech sítě?** V horku spíte při otevřeném okně a bez sítí to u lesa a u vody nejde vydržet.
- **Jak daleko je voda?** Do deseti minut autem je pořád spontánní, půl hodiny už je výprava, na kterou se v čtyřiceti stupních vykašlete.
- **Je bazén nebo kádě k dispozici?** V tropickém týdnu je vlastní bazén rozdíl mezi „vydrželi jsme" a „bylo to skvělé".

::nabidky{facility="pool" text="Prohlédnout pobyty s bazénem"}

## Program na tropický den

V horku se den nedělí na dopoledne a odpoledne, ale na to, kdy je stín. Vyplatí se otočit rytmus: brzy ráno ven, v poledne dovnitř, večer znovu ven.

- **Do desíti ráno.** Túra, rozhledna, kolo. V sedm ráno je i v srpnu příjemně a máte trasu sami pro sebe.
- **Poledne a odpoledne.** Voda, les nebo vnitřek. Jeskyně mají celoročně kolem osmi stupňů, hrady a zámky tlusté zdi – obojí je v tropech legitimní program, ne nouzovka.
- **Po sedmé večer.** Zahrada, gril, koupání ve vodě, která je po celodenním slunci nejteplejší. Tohle je hodina, kvůli které se na chalupu jezdí.

## Nejbližší volný termín

Vedro se dá přečkat, nebo se z něj dá odjet. Na dvě noci na horách nepotřebujete plán ani dovolenou:

::cta{text="Najít volný termín na tento víkend"}`,
}
